import React, { useState } from "react";
import { X, Award, Loader2, Image as ImageIcon, FileDown, Share2, Link as LinkIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";

export default function DiplomaFetchModal({ mode, lang, onClose }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [share, setShare] = useState(null); // { id, name } for student share panel
  const [teacherReady, setTeacherReady] = useState(null); // teacher email validated -> show download panel

  const pt = lang !== "en";
  const isStudent = mode === "student";
  const title = isStudent
    ? (pt ? "Obter o meu diploma" : "Get my diploma")
    : (pt ? "Diplomas dos meus alunos" : "My students' diplomas");

  const teacherMessageFor = (status) => {
    if (status === 400) return pt ? "Email inválido." : "Invalid email.";
    if (status === 404) return pt ? "Não foram encontrados diplomas para este email." : "No diplomas found for this email.";
    return pt ? "Ocorreu um erro. Tenta novamente." : "An error occurred. Please try again.";
  };

  const submitStudent = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/diploma/student/lookup", {
        username: username.trim(), password: password.trim(),
      }, { params: { lang } });
      setShare({ id: data.participation_id, name: data.name });
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) setError(pt ? "Credenciais inválidas." : "Invalid credentials.");
      else if (status === 403) setError(err.response?.data?.detail || (pt ? "Diploma indisponível. Volta mais tarde!" : "Diploma unavailable. Come back later!"));
      else if (status === 404) setError(pt ? "Ainda não concluíste nenhum concurso." : "You have not completed any contest yet.");
      else setError(pt ? "Ocorreu um erro. Tenta novamente." : "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitTeacher = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Validate first (availability / email / has diplomas) — then show the download panel.
      await api.post("/diploma/teacher/check", { email: email.trim() }, { params: { lang } });
      setTeacherReady(email.trim());
    } catch (err) {
      const status = err.response?.status;
      let msg = teacherMessageFor(status);
      const data = err.response?.data;
      if (data?.detail) {
        msg = data.detail;
      } else if (data && typeof data.text === "function") {
        try { const j = JSON.parse(await data.text()); if (j.detail) msg = j.detail; } catch (_) {}
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = share ? `${window.location.origin}/diploma/view/${share.id}` : "";
  const imgUrl = share ? `${API_BASE}/diploma/${share.id}/image?lang=${lang}` : "";
  const pdfUrl = share ? `${API_BASE}/diploma/${share.id}?lang=${lang}` : "";
  const waText = share
    ? encodeURIComponent((pt ? `🎄 Vê o meu diploma do NATALMÁTICO! ` : `🎄 Check out my X-MATHS Challenge diploma! `) + shareUrl)
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(pt ? "Link copiado!" : "Link copied!");
    } catch (_) {
      const ta = document.createElement("textarea");
      ta.value = shareUrl; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      toast.success(pt ? "Link copiado!" : "Link copied!");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1D3A]/60 backdrop-blur-sm px-4"
      data-testid="diploma-fetch-modal"
      onClick={onClose}
    >
      <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          data-testid="diploma-modal-close"
          className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0B1D3A]/5 hover:bg-[#0B1D3A]/10 text-[#0B1D3A]"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-3 mb-5 pr-10">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FFD93D] text-[#0B1D3A]">
            <Award size={20} />
          </span>
          <h2 className="font-fredoka text-2xl font-bold text-[#0B1D3A]">{title}</h2>
        </div>

        {teacherReady ? (
          <div className="space-y-4" data-testid="teacher-download-panel">
            <p className="font-nunito text-sm text-[#0B1D3A]/70">
              {pt
                ? "Diplomas encontrados! Descarrega todos os diplomas da tua turma:"
                : "Diplomas found! Download all your class diplomas:"}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <a
                href={`${API_BASE}/diploma/teacher/pdf?email=${encodeURIComponent(teacherReady)}&lang=${lang}`}
                target="_blank" rel="noreferrer" download
                data-testid="teacher-download-pdf"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#005F73] text-white font-fredoka font-bold py-3 hover:bg-[#00485a] transition-colors"
              >
                <FileDown size={18} /> PDF
              </a>
              <a
                href={`${API_BASE}/diploma/teacher/png?email=${encodeURIComponent(teacherReady)}&lang=${lang}`}
                target="_blank" rel="noreferrer" download
                data-testid="teacher-download-png"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#0B1D3A] text-white font-fredoka font-bold py-3 hover:bg-[#13294f] transition-colors"
              >
                <ImageIcon size={18} /> PNG (.zip)
              </a>
            </div>
            <p className="font-nunito text-xs text-[#0B1D3A]/50">
              {pt
                ? "PDF: um único ficheiro com todos os diplomas. PNG: um .zip com uma imagem por aluno."
                : "PDF: one file with all diplomas. PNG: a .zip with one image per student."}
            </p>
            <button
              onClick={() => { setTeacherReady(null); setEmail(""); }}
              data-testid="teacher-download-back"
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-[#0B1D3A]/15 text-[#0B1D3A] font-bold py-2.5 hover:bg-[#0B1D3A]/5 transition-colors"
            >
              {pt ? "Outro email" : "Another email"}
            </button>
          </div>
        ) : share ? (
          <div className="space-y-4" data-testid="diploma-share-panel">
            <img src={imgUrl} alt="diploma" className="w-full rounded-2xl border border-[#0B1D3A]/10 shadow-sm" data-testid="diploma-share-image" />
            <div className="grid grid-cols-2 gap-3">
              <a href={imgUrl} target="_blank" rel="noreferrer" data-testid="diploma-download-png"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#0B1D3A] text-white font-fredoka font-bold py-2.5 hover:bg-[#13294f] transition-colors">
                <ImageIcon size={16} /> PNG
              </a>
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="diploma-download-pdf"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#005F73] text-white font-fredoka font-bold py-2.5 hover:bg-[#00485a] transition-colors">
                <FileDown size={16} /> PDF
              </a>
            </div>
            <a href={`https://wa.me/?text=${waText}`} target="_blank" rel="noreferrer" data-testid="diploma-share-whatsapp"
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] text-white font-fredoka font-bold py-3 text-lg hover:bg-[#1eb957] transition-colors">
              <Share2 size={18} /> {pt ? "Partilhar no WhatsApp" : "Share on WhatsApp"}
            </a>
            <div className="flex gap-3">
              <button onClick={copyLink} data-testid="diploma-copy-link"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-[#0B1D3A]/15 text-[#0B1D3A] font-bold py-2.5 hover:bg-[#0B1D3A]/5 transition-colors">
                <LinkIcon size={16} /> {pt ? "Copiar link" : "Copy link"}
              </button>
              <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="diploma-open-page"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-[#0B1D3A]/15 text-[#0B1D3A] font-bold py-2.5 hover:bg-[#0B1D3A]/5 transition-colors">
                <ExternalLink size={16} /> {pt ? "Ver página" : "View page"}
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={isStudent ? submitStudent : submitTeacher} className="space-y-4">
            {isStudent ? (
              <>
                <div>
                  <label className="block font-nunito font-bold text-sm text-[#0B1D3A] mb-1">
                    {pt ? "Nome de utilizador" : "Username"}
                  </label>
                  <input
                    data-testid="diploma-student-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    className="w-full rounded-2xl border-2 border-[#0B1D3A]/15 px-4 py-2.5 font-nunito focus:border-[#FF6B6B] outline-none"
                    placeholder="00000000"
                  />
                </div>
                <div>
                  <label className="block font-nunito font-bold text-sm text-[#0B1D3A] mb-1">
                    {pt ? "Palavra-passe" : "Password"}
                  </label>
                  <input
                    data-testid="diploma-student-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full rounded-2xl border-2 border-[#0B1D3A]/15 px-4 py-2.5 font-nunito focus:border-[#FF6B6B] outline-none"
                    placeholder="0000"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block font-nunito font-bold text-sm text-[#0B1D3A] mb-1">
                  {pt ? "Email do professor" : "Teacher email"}
                </label>
                <input
                  data-testid="diploma-teacher-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-2xl border-2 border-[#0B1D3A]/15 px-4 py-2.5 font-nunito focus:border-[#FF6B6B] outline-none"
                  placeholder="professor@escola.pt"
                />
              </div>
            )}

            {error && (
              <p data-testid="diploma-modal-error" className="font-nunito text-sm text-[#FF6B6B] font-semibold">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="diploma-modal-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FF6B6B] text-white font-fredoka font-bold py-3 text-lg shadow-lg hover:bg-[#ff5252] transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {isStudent
                ? (pt ? "Obter diploma" : "Get diploma")
                : (pt ? "Obter diplomas" : "Get diplomas")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
